import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { HistoryTable } from "@/pages/employees/employee/tables/HistoryTable.tsx";
import { PendingTableCell } from "@/pages/employees/employee/tables/PendingTableCell.tsx";
import {
  createExpandableTextCell,
  createHistoricalDateCell,
  createTextCell,
} from "@/pages/employees/employee/tables/table-cell-factory.utils.tsx";
import { EmployeeEmploymentEditModal } from "@/pages/employees/employee/widgets/EmployeeEmploymentEditModal.tsx";
import { EmployeeTerminationEditModal } from "@/pages/employees/employee/widgets/EmployeeTerminationEditModal.tsx";
import { EmployeeTerminationInfoModal } from "@/pages/employees/employee/widgets/EmployeeTerminationInfoModal.tsx";
import { useEmployeeEmploymentHistory } from "@/queries/employees/use-employee-employment-history.query.ts";
import { useEmployeeEmploymentDelete } from "@/queries/employees/use-employee-employment.query.ts";
import { useEmployeeTerminationDelete } from "@/queries/employees/use-employee-termination.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiEmployeeEmploymentHistoryItem,
  ApiEmployeeEmploymentHistoryItemType,
  ApiEmployeeEmploymentHistoryPendingItem,
} from "@/types/employees/employee-employment-history.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import {
  Button,
  ButtonIcon,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

type EmploymentHistoryRow =
  | (ApiEmployeeEmploymentHistoryItem & { pending?: never })
  | ApiEmployeeEmploymentHistoryPendingItem;

export const EmployeeWorkInfoEmployment = ({
  employee,
  tableScrollBoundaryRef,
}: EmployeePageComponentProps) => {
  const { t } = useTranslation();
  const { canEditField, canViewField, canViewAnyOfFields, canViewCustomTableFieldForEmployee } =
    usePermissions();
  const { renderDate, numberToFixed } = useFormatter();
  const { openModal } = useModals();
  const { table, fields } = useSystemTableConfig(ApiTableTypes.employee_employments, "view");
  const { renderFieldDataInCell } = useCustomFieldsHook();

  const canViewHistory = useMemo(
    () => canViewAnyOfFields(employee, [EmployeeFieldPermission.employee_employments_history]),
    [employee, canViewAnyOfFields]
  );
  const canManageEmployment = canEditField(
    employee,
    EmployeeFieldPermission.manage_employee_employment
  );
  const canManageTermination = canEditField(
    employee,
    EmployeeFieldPermission.manage_employee_termination
  );
  const canManageRow = useCallback(
    (item: EmploymentHistoryRow) =>
      item.type === ApiEmployeeEmploymentHistoryItemType.termination
        ? canManageTermination
        : canManageEmployment,
    [canManageEmployment, canManageTermination]
  );

  const {
    data: employeeHistory,
    isLoading,
    isFetching,
  } = useEmployeeEmploymentHistory(employee.id, canViewHistory);

  const employeeEmploymentDelete = useEmployeeEmploymentDelete();
  const employeeTerminationDelete = useEmployeeTerminationDelete();
  const currentEmploymentId = employee.employee_employment?.id;
  const currentTerminationId = employee.employee_termination?.id;

  const isCurrentEmployment = useCallback(
    (item: EmploymentHistoryRow) =>
      !item.pending &&
      ((item.type === ApiEmployeeEmploymentHistoryItemType.employment &&
        item.employment?.id === currentEmploymentId) ||
        (item.type === ApiEmployeeEmploymentHistoryItemType.termination &&
          item.termination?.id === currentTerminationId)),
    [currentEmploymentId, currentTerminationId]
  );

  const getTextClassName = useCallback(
    (item: EmploymentHistoryRow) => (isCurrentEmployment(item) ? "text-title" : "text-secondary"),
    [isCurrentEmployment]
  );

  const handleAdd = useCallback(() => {
    if (!employee) return;

    openModal(EmployeeEmploymentEditModal, {
      employee,
    });
  }, [employee]);

  const handleEdit = useCallback(
    (item: ApiEmployeeEmploymentHistoryItem) => {
      if (!canManageRow(item)) return;

      if (item.type === ApiEmployeeEmploymentHistoryItemType.termination) {
        openModal(EmployeeTerminationEditModal, {
          employee,
          employeeTermination: item.termination,
        });
      } else {
        openModal(EmployeeEmploymentEditModal, {
          employee,
          employeeEmployment: item.employment,
        });
      }
    },
    [employee, canManageRow, openModal]
  );

  const handleDeleteAction = useCallback(
    (item: ApiEmployeeEmploymentHistoryItem) => {
      if (!canManageRow(item)) return;

      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          if (item.type === ApiEmployeeEmploymentHistoryItemType.termination) {
            employeeTerminationDelete.mutate(
              { employeeId: employee.id, positionId: item.termination.id },
              {
                onSuccess: () => {
                  analyticsService.trackEvent(analyticEvents.employeeEmployments.deleted, {
                    [analyticProperties.id]: employee.id,
                  });
                },
              }
            );
          } else {
            employeeEmploymentDelete.mutate(
              { employeeId: employee.id, positionId: item.employment.id },
              {
                onSuccess: () => {
                  analyticsService.trackEvent(analyticEvents.employeeEmployments.deleted, {
                    [analyticProperties.id]: employee.id,
                  });
                },
              }
            );
          }
        },
      });
    },
    [employee, canManageRow, openModal, employeeEmploymentDelete, employeeTerminationDelete, t]
  );

  const displayData = useMemo<EmploymentHistoryRow[]>(
    () => [...(employeeHistory?.pending_rows ?? []), ...(employeeHistory?.items ?? [])],
    [employeeHistory]
  );

  const columnHelper = createColumnHelper<EmploymentHistoryRow>();
  const columns = useMemo<ColumnDef<EmploymentHistoryRow, unknown>[]>(() => {
    const inner: ColumnDef<EmploymentHistoryRow, unknown>[] = [];

    if (canViewField(employee, EmployeeFieldPermission.employee_employment_date)) {
      inner.push(
        columnHelper.display({
          header: t("common:date"),
          cell: (cell) => {
            const row = cell.row.original;
            if (row.pending) {
              return (
                <PendingTableCell pending={row.pending}>
                  {row.date_effective_from ? renderDate(row.date_effective_from) : "-"}
                </PendingTableCell>
              );
            }
            return createHistoricalDateCell<EmploymentHistoryRow>({
              getValue: (item) =>
                item.date_effective_from ? renderDate(item.date_effective_from) : null,
              getClassName: getTextClassName,
              getDate: (item) => item.date_effective_from,
            })(cell);
          },
          id: ApiFieldTypes.effective_from,
          ...ColumnSizes.historicalDate,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_employment_legal_entity)) {
      inner.push(
        columnHelper.display({
          header: t("core:legal_entity"),
          cell: createTextCell<EmploymentHistoryRow>({
            getValue: (item) => item.employment?.legal_entity?.title,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_employment_legal_entity,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_employment_contract)) {
      inner.push(
        columnHelper.display({
          header: t("core:contract"),
          cell: createTextCell<EmploymentHistoryRow>({
            getValue: (item) => {
              if (item.termination) {
                const termination = item.termination;
                return (
                  <Stack justify={"between"} direction={"horizontal"} gap={"smd"}>
                    <Text className={"text-danger font-semibold"}>
                      {t("core:employment_ended")}
                    </Text>
                    <ButtonIcon
                      variant="secondary"
                      className={
                        canManageTermination
                          ? "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          : undefined
                      }
                      icon={<FontAwesomeIcon icon="fa-light fa-circle-info" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(EmployeeTerminationInfoModal, { employee, termination });
                      }}
                    />
                  </Stack>
                );
              }

              return item.employment?.employment_contract?.title;
            },
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_employment_contract,
          ...ColumnSizes.middle,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_employment_employment_type)) {
      inner.push(
        columnHelper.display({
          header: t("core:employment_type"),
          cell: createTextCell<EmploymentHistoryRow>({
            getValue: (item) => item.employment?.employment_type?.title,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_employment_type,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_employment_work_pattern)) {
      inner.push(
        columnHelper.display({
          header: t("core:work_pattern"),
          cell: createTextCell<EmploymentHistoryRow>({
            getValue: (item) => item.employment?.work_pattern?.title,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_employment_work_pattern,
          ...ColumnSizes.standard,
        })
      );

      inner.push(
        columnHelper.display({
          header: t("core:fte"),
          cell: createTextCell<EmploymentHistoryRow>({
            getValue: (item) =>
              item.employment?.contractual_fte != null
                ? numberToFixed(item.employment.contractual_fte)
                : null,
            getClassName: getTextClassName,
          }),
          id: "employee_employment_fte",
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_employment_probation)) {
      inner.push(
        columnHelper.display({
          header: t("core:probation_till"),
          cell: createTextCell<EmploymentHistoryRow>({
            getValue: (item) =>
              item.employment?.date_probation_ends
                ? renderDate(item.employment.date_probation_ends)
                : null,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_employment_probation_date_end,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_employment_comment)) {
      inner.push(
        columnHelper.display({
          header: t("common:comments"),
          cell: createExpandableTextCell<EmploymentHistoryRow>({
            getValue: (item) => item.employment?.comment,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.employee_employment_comment,
          ...ColumnSizes.standard,
        })
      );
    }

    if (canViewField(employee, EmployeeFieldPermission.employee_employment_date)) {
      inner.push(
        columnHelper.display({
          header: t("common:date_effective_to"),
          cell: createTextCell<EmploymentHistoryRow>({
            getValue: (item) =>
              item.employment?.date_effective_to
                ? renderDate(item.employment.date_effective_to)
                : null,
            getClassName: getTextClassName,
          }),
          id: ApiFieldTypes.effective_to,
          ...ColumnSizes.standard,
        })
      );
    }

    const configColumns: ColumnDef<EmploymentHistoryRow, unknown>[] = fields.flatMap((field) => {
      if (field.type === ApiFieldTypes.custom) {
        if (!table || !canViewCustomTableFieldForEmployee(employee, table.id, field.slug)) {
          return [];
        }
        return [
          columnHelper.display({
            header: field.title,
            cell: ({ row }) => {
              const customFields = row.original.employment?.custom_fields ?? [];
              return renderFieldDataInCell(field, customFields);
            },
            id: field.slug,
            ...ColumnSizes.standard,
          }),
        ];
      }

      if (
        field.type === ApiFieldTypes.changed_by &&
        canViewField(employee, EmployeeFieldPermission.employee_employment_changed_by)
      ) {
        return [
          columnHelper.display({
            header: field.title?.trim() || t("core:changed_by"),
            cell: ({ row }) =>
              row.original.changed_by ? (
                <EmployeeHoverCard
                  employee={row.original.changed_by}
                  position={getEmployeePositionTitle(row.original.changed_by)}
                >
                  <EmployeeItem employee={row.original.changed_by} showPosition={false} size="sm" />
                </EmployeeHoverCard>
              ) : null,
            id: field.type,
            ...ColumnSizes.standard,
          }),
        ];
      }

      const column = inner.find((item) => item.id === field.type);
      const fallbackTitle = typeof column?.header === "string" ? column.header : "";
      return column ? [{ ...column, header: field.title?.trim() || fallbackTitle }] : [];
    });

    return configColumns;
  }, [
    employee,
    canViewField,
    canManageTermination,
    getTextClassName,
    renderDate,
    numberToFixed,
    openModal,
    handleEdit,
    handleDeleteAction,
    employeeEmploymentDelete.isPending,
    employeeTerminationDelete.isPending,
    t,
    fields,
    table,
    canViewCustomTableFieldForEmployee,
    renderFieldDataInCell,
  ]);

  const renderRowAction = useCallback(
    (row: Row<EmploymentHistoryRow>) => {
      if (row.original.pending || !canManageRow(row.original)) return null;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => handleEdit(row.original as ApiEmployeeEmploymentHistoryItem)}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            onClick={() => handleDeleteAction(row.original as ApiEmployeeEmploymentHistoryItem)}
            variant="menuDanger"
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      );
    },
    [handleEdit, handleDeleteAction, canManageRow, t]
  );

  const displayCount = useMemo(() => {
    if (!canViewHistory) return employee.employee_employment ? 1 : 0;
    return displayData.length;
  }, [canViewHistory, employee.employee_employment, displayData.length]);

  const currentItem = useMemo<ApiEmployeeEmploymentHistoryItem | undefined>(() => {
    if (!currentEmploymentId) return undefined;

    return employeeHistory?.items.find(
      (item) =>
        item.type === ApiEmployeeEmploymentHistoryItemType.employment &&
        item.employment?.id === currentEmploymentId
    );
  }, [employeeHistory, currentEmploymentId]);

  return (
    <Stack gap="lg">
      <Stack
        className="justify-between"
        direction="horizontal"
        items="center"
        gap="md"
        data-employee-table-heading
      >
        <Title
          header={t("core:employment")}
          size="lg"
          badge={displayCount > 1 ? `(${displayCount})` : undefined}
          badgeVariant="secondary"
        />
        {canManageEmployment && (
          <Button
            onClick={() => handleAdd()}
            variant="secondary"
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:add_record")}
          </Button>
        )}
      </Stack>
      <HistoryTable<EmploymentHistoryRow>
        currentItem={currentItem}
        historyItems={displayData}
        columns={columns}
        canViewHistory={canViewHistory}
        isLoading={isLoading}
        isFetching={
          isFetching || employeeEmploymentDelete.isPending || employeeTerminationDelete.isPending
        }
        emptyStateTitle={t("core:no_employment_assigned")}
        scrollLayout="notion"
        scrollBoundaryRef={tableScrollBoundaryRef}
        stickyLastColumn={false}
        renderRowAction={canManageEmployment || canManageTermination ? renderRowAction : undefined}
      />
    </Stack>
  );
};
